import React, { useState } from "react";
import {
  Plus,
  Trash2,
  Folder,
  ExternalLink,
  FolderOpen,
  Loader,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useCategories } from "../../hooks/useCategories";
import DeleteCategoryModal from "../../modals/categoryModal/deleteCategory";
import AddCategoryModal from "../../modals/categoryModal/addCategory";
import EditCategoryModal from "../../modals/categoryModal/editCategory";

const Categories = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { categories, loading, error, pagination } = useCategories(currentPage);

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  const handleAddClick = () => {
    setAddModalOpen(true);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setEditModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8">
      <div className="mx-auto">
        {/* --- HEADER --- */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4 sm:mb-8">
          <div>
            <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
              Categories
            </h1>
            <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
              Organize your products into categories
            </p>
          </div>
          <button
            onClick={handleAddClick}
            className="flex items-center gap-2 bg-[#D2BDA1] text-black text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100 w-full sm:w-auto justify-center"
          >
            <Plus size={20} strokeWidth={3} /> Add Category
          </button>
        </div>

        {/* --- CATEGORY GRID --- */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader size={32} className="text-[#6B7143] animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-4 rounded-lg text-xs sm:text-sm">
            {error}
          </div>
        ) : categories.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col"
                >
                  {/* Image Area */}
                  <div className="relative h-40 sm:h-64 w-full">
                    {cat.image_path ? (
                      <img
                        src={cat.image_path}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F3F1ED] to-[#E8E4DD] flex items-center justify-center">
                        <Folder size={48} className="text-[#BFAC93]" />
                      </div>
                    )}
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteClick(cat)}
                      className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-white p-2 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors shadow-md active:scale-90 duration-100"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="mb-2 sm:mb-4">
                        <h3 className="text-base sm:text-xl font-medium text-[#0C0C0E] break-words">
                          {cat.name}
                        </h3>
                        <p className="text-[#7D828D] text-xs sm:text-sm mt-1 break-words">
                          {cat.description || "No description"}
                        </p>
                      </div>

                      {/* Product Count Row */}
                      <div className="flex justify-between items-center py-1 border-y border-[#F1F2F3]">
                        <span className="font-medium text-[#0C0C0E] text-xs sm:text-sm">
                          {cat.products_count} products
                        </span>
                        <Folder className="text-[#BFAC93]" size={16} />
                      </div>
                    </div>

                    {/* Footer Action */}
                    <button
                      onClick={() => handleEditClick(cat)}
                      className="mt-4 sm:mt-6 w-full py-2 sm:py-2.5 border border-slate-200 rounded-xl text-[#0C0C0E] font-medium text-xs sm:text-sm flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors bg-gradient-to-b from-white to-[#F3F3F3] shadow-sm active:scale-95 active:shadow-none duration-100"
                    >
                      <ExternalLink size={16} /> Edit Category
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 sm:mt-14 flex justify-center items-center">
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={pagination?.last_page || 1}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6">
            <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
              <FolderOpen size={32} className="text-[#BFAC93]" />
            </div>
            <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
              No categories yet
            </h4>
            <p className="text-xs sm:text-sm text-slate-400 text-center">
              Create your first category to organize products
            </p>
          </div>
        )}
      </div>

      {selectedCategory && (
        <DeleteCategoryModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDeleteSuccess={() => setCurrentPage(1)}
          category={selectedCategory}
        />
      )}

      <AddCategoryModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => setCurrentPage(1)}
      />

      {selectedCategory && (
        <EditCategoryModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => setCurrentPage(1)}
          category={selectedCategory}
        />
      )}
    </div>
  );
};

export default Categories;
